import { useState, useEffect } from 'react';
import api from '../../services/api';
import { X } from 'lucide-react';
import WheelPicker from '../shared/WheelPicker';

export default function EditVideoModal({ isOpen, onClose, onSuccess, video, sections = [] }) {
  const [formData, setFormData] = useState({
    title_pt: '',
    description_pt: '',
    order_index: 0,
    section_id: null,
    is_published: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (video) {
      setFormData({
        title_pt: video.title_pt || '',
        description_pt: video.description_pt || '',
        order_index: video.order_index || 0,
        section_id: video.section_id || null,
        is_published: video.is_published || false
      });
    }
  }, [video]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSend = {
        title_pt: formData.title_pt,
        title_en: formData.title_pt,
        description_pt: formData.description_pt,
        description_en: formData.description_pt,
        order_index: formData.order_index,
        is_published: formData.is_published,
        section_id: formData.section_id
      };

      console.log('📤 Enviando dados:', dataToSend);

      const { data } = await api.put(`/videos/${video.id}`, dataToSend);
      console.log('✅ Vídeo atualizado:', data);
      onSuccess(data);
      onClose();
    } catch (err) {
      console.error('❌ Erro ao atualizar vídeo:', err);
      setError(err.response?.data?.details || err.response?.data?.error || 'Erro ao atualizar vídeo');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-background-800 border border-gray-700 rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-text-primary">
            Editar Vídeo
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-2xl text-red-400">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Título do Vídeo
            </label>
            <input
              type="text"
              value={formData.title_pt}
              onChange={(e) => setFormData({...formData, title_pt: e.target.value})}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary"
              placeholder="Ex: Aula 01 - Introdução"
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Descrição
            </label>
            <textarea
              value={formData.description_pt}
              onChange={(e) => setFormData({...formData, description_pt: e.target.value})}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary"
              rows="4"
              placeholder="Descreva o conteúdo do vídeo..."
            />
          </div>

          {/* Seção */}
          {sections.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Seção (opcional)
              </label>
              <select
                value={formData.section_id || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({...formData, section_id: value === '' ? null : value});
                }}
                className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary cursor-pointer"
              >
                <option value="">Sem seção</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>
                    {section.title_pt}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Organize o vídeo em uma seção específica
              </p>
            </div>
          )}

          {/* Ordem */}
          <WheelPicker
            value={formData.order_index}
            onChange={(newValue) => setFormData({...formData, order_index: newValue})}
            min={1}
            max={50}
            label="Ordem de Exibição"
          />

          {/* Publicado */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
              className="w-5 h-5 rounded border-2 border-gray-700 bg-background-900 checked:bg-primary-500 checked:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all"
            />
            <label htmlFor="is_published" className="text-sm font-medium text-text-primary cursor-pointer">
              Publicar vídeo
            </label>
          </div>

          {/* Botões */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-background-700 hover:bg-background-600 text-text-primary px-6 py-3 rounded-2xl font-semibold transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl font-semibold transition-all disabled:opacity-50 shadow-lg shadow-primary-500/20"
            >
              {loading ? 'Salvando...' : 'Atualizar Vídeo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
